import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/* Lightweight cookie consent — frontend-only.
   This site uses no analytics, advertising or tracking cookies; the only
   thing stored is the visitor's own preference (in their browser's
   localStorage). The banner reopens via the footer's "Cookie Settings".
   (No prior FIAD component exists in this environment; this is the
   equivalent implementation, styled to this site's identity.) */

const STORAGE_KEY = "cdp-cookie-consent-v1";

const CATEGORIES = [
    {
        name: "Strictly necessary",
        desc: "Core site functions and security supplied by the website platform.",
        state: "Always on",
        locked: true,
    },
    {
        name: "Your consent preference",
        desc: "Remembers the choice you make here, stored only in your own browser.",
        state: "Always on",
        locked: true,
    },
    {
        name: "Analytics / performance",
        desc: "Not used on this website.",
        state: "Not in use",
        locked: true,
    },
    {
        name: "Advertising / marketing",
        desc: "Not used on this website.",
        state: "Not in use",
        locked: true,
    },
];

export const CookieConsent = () => {
    const [open, setOpen] = useState(false);
    const [showPrefs, setShowPrefs] = useState(false);

    useEffect(() => {
        let saved = null;
        try {
            saved = window.localStorage.getItem(STORAGE_KEY);
        } catch {
            saved = null;
        }
        if (!saved) {
            const t = setTimeout(() => setOpen(true), 1600);
            return () => clearTimeout(t);
        }
    }, []);

    useEffect(() => {
        const reopen = () => {
            setShowPrefs(true);
            setOpen(true);
        };
        window.addEventListener("cdp:open-cookie-settings", reopen);
        return () => window.removeEventListener("cdp:open-cookie-settings", reopen);
    }, []);

    const save = (choice) => {
        try {
            window.localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ choice, savedAt: new Date().toISOString() })
            );
        } catch {
            /* storage unavailable — banner simply reappears next visit */
        }
        setOpen(false);
        setShowPrefs(false);
    };

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-label="Cookie preferences"
            aria-live="polite"
            className="cookie-banner fixed inset-x-4 bottom-4 z-40 mx-auto max-w-xl rounded-md p-6 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md"
            data-testid="cookie-banner"
        >
            <h2 className="font-display text-xl font-semibold text-[#FFF5E6]">
                A note on cookies
            </h2>
            <p className="mt-3 text-sm font-light leading-relaxed text-[#FFF5E6]/75">
                This website uses only essential cookies and remembers your preference in your own
                browser. No analytics, advertising or tracking cookies are used. Read our{" "}
                <Link
                    to="/cookie-policy"
                    className="text-[#C8A15A] underline-offset-4 hover:underline"
                    data-testid="cookie-policy-link"
                >
                    Cookie Policy
                </Link>
                .
            </p>

            {showPrefs && (
                <div className="mt-5 space-y-2.5" data-testid="cookie-preferences">
                    {CATEGORIES.map((c) => (
                        <div
                            key={c.name}
                            className="cookie-toggle flex items-center justify-between gap-4 px-4 py-3"
                        >
                            <div>
                                <p className="text-sm font-medium text-[#FFF5E6]">{c.name}</p>
                                <p className="mt-0.5 text-xs font-light text-[#FFF5E6]/55">{c.desc}</p>
                            </div>
                            <span
                                className={`shrink-0 text-[0.62rem] uppercase tracking-[0.16em] ${
                                    c.state === "Always on" ? "text-[#C8A15A]" : "text-[#FFF5E6]/40"
                                }`}
                            >
                                {c.state}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    className="btn btn-gold !px-6 !py-2.5"
                    onClick={() => save("accepted")}
                    data-testid="cookie-accept-button"
                >
                    Accept
                </button>
                {showPrefs ? (
                    <button
                        type="button"
                        className="btn btn-outline !px-6 !py-2.5"
                        onClick={() => save("essential-only")}
                        data-testid="cookie-save-button"
                    >
                        Save preferences
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-outline !px-6 !py-2.5"
                        onClick={() => setShowPrefs(true)}
                        data-testid="cookie-preferences-button"
                    >
                        Preferences
                    </button>
                )}
            </div>
        </div>
    );
};
